import _defineProperty from "@babel/runtime/helpers/defineProperty";
import jsonStringfy from 'fast-json-stable-stringify';
import Fetcher from '../fetcher';
import { ResponseError } from '../fetcher/errors';
import { ClientCauseReason, ClientUserState } from '../fetcher/types';
import getLogger from '../logger';
import MetadataManager from './MetadataManager';
import { RefreshStatus } from './types';
import { flagResponseToFlagUpdate } from './util';
export const SCHEDULER_OPTIONS_DEFAULT = {
  minWaitInterval: 300000,
  // 300 second = 5 mins
  maxWaitInterval: 1200000,
  // 1200 second = 20 mins
  backOffFactor: 2,
  backOffJitter: 0.1,
  // Jitter is upto 10% of wait time
  interval: 300000,
  // 300 second = 5 mins
  tabHiddenPollingFactor: 12,
  // for hidden tabs, use hiddenTabFactor * interval for polling = 1 hour (default)
  maxInstantRetryTimes: 0
};
export const NO_CACHE_RETRY_OPTIONS_DEFAULT = {
  ...SCHEDULER_OPTIONS_DEFAULT,
  minWaitInterval: 500,
  interval: 500,
  maxInstantRetryTimes: 1
};
export default class Refresh {
  constructor(apiKey, env, user, onFeatureFlagUpdate, pollingConfig, version, lastUpdateTimestamp = 0, onError = () => {}, noCachePollingConfig, useGatewayURL = false, perimeter) {
    _defineProperty(this, "failureCount", 0);
    this.logger = getLogger();
    this.user = user;
    this.onFeatureFlagUpdate = onFeatureFlagUpdate;
    this.version = version;
    this.lastUpdateTimestamp = lastUpdateTimestamp;
    this.pollingConfig = {
      minWaitInterval: (pollingConfig === null || pollingConfig === void 0 ? void 0 : pollingConfig.minWaitInterval) || SCHEDULER_OPTIONS_DEFAULT.minWaitInterval,
      maxWaitInterval: (pollingConfig === null || pollingConfig === void 0 ? void 0 : pollingConfig.maxWaitInterval) || SCHEDULER_OPTIONS_DEFAULT.maxWaitInterval,
      backOffFactor: (pollingConfig === null || pollingConfig === void 0 ? void 0 : pollingConfig.backOffFactor) || SCHEDULER_OPTIONS_DEFAULT.backOffFactor,
      backOffJitter: (pollingConfig === null || pollingConfig === void 0 ? void 0 : pollingConfig.backOffJitter) !== undefined ? pollingConfig.backOffJitter : SCHEDULER_OPTIONS_DEFAULT.backOffJitter,
      interval: (pollingConfig === null || pollingConfig === void 0 ? void 0 : pollingConfig.interval) || SCHEDULER_OPTIONS_DEFAULT.interval,
      tabHiddenPollingFactor: (pollingConfig === null || pollingConfig === void 0 ? void 0 : pollingConfig.tabHiddenPollingFactor) || SCHEDULER_OPTIONS_DEFAULT.tabHiddenPollingFactor,
      maxInstantRetryTimes: 0 // Force this to 0 so this never reschdules instantly with cache
    };

    this.noCachePollingConfig = {
      minWaitInterval: (noCachePollingConfig === null || noCachePollingConfig === void 0 ? void 0 : noCachePollingConfig.minWaitInterval) || NO_CACHE_RETRY_OPTIONS_DEFAULT.minWaitInterval,
      maxWaitInterval: (noCachePollingConfig === null || noCachePollingConfig === void 0 ? void 0 : noCachePollingConfig.maxWaitInterval) || NO_CACHE_RETRY_OPTIONS_DEFAULT.maxWaitInterval,
      backOffFactor: (noCachePollingConfig === null || noCachePollingConfig === void 0 ? void 0 : noCachePollingConfig.backOffFactor) || NO_CACHE_RETRY_OPTIONS_DEFAULT.backOffFactor,
      backOffJitter: (noCachePollingConfig === null || noCachePollingConfig === void 0 ? void 0 : noCachePollingConfig.backOffJitter) !== undefined ? noCachePollingConfig.backOffJitter : NO_CACHE_RETRY_OPTIONS_DEFAULT.backOffJitter,
      interval: (noCachePollingConfig === null || noCachePollingConfig === void 0 ? void 0 : noCachePollingConfig.interval) || NO_CACHE_RETRY_OPTIONS_DEFAULT.interval,
      tabHiddenPollingFactor: (pollingConfig === null || pollingConfig === void 0 ? void 0 : pollingConfig.tabHiddenPollingFactor) || SCHEDULER_OPTIONS_DEFAULT.tabHiddenPollingFactor,
      maxInstantRetryTimes: (noCachePollingConfig === null || noCachePollingConfig === void 0 ? void 0 : noCachePollingConfig.maxInstantRetryTimes) !== undefined ? noCachePollingConfig.maxInstantRetryTimes : NO_CACHE_RETRY_OPTIONS_DEFAULT.maxInstantRetryTimes
    };
    this.onError = onError;
    this.fetcher = new Fetcher(apiKey, env, undefined, useGatewayURL, perimeter);
    this.metadataManager = new MetadataManager(this.pollingConfig);
    this.status = RefreshStatus.INITIALISED;
    this.updateMetadataOnInitialisation();
    this.visibilityChangeHandler = this.visibilityChangeHandler.bind(this);
  }

  // start refresh by fetchAndReschedule
  start() {
    this.stop();
    this.bindVisibilityChange();
    this.fetchAndReschedule();
  }

  // cancel pending schedule
  stop() {
    if (this.timerId) {
      window.clearTimeout(this.timerId);
      this.timerId = undefined;
      this.unbindVisibilityChange();
    }
  }

  // update user, with optional version and timestamp
  updateUserContext(user, version, lastUpdateTimestamp = 0) {
    this.user = user;
    this.version = version;
    this.lastUpdateTimestamp = lastUpdateTimestamp;
    this.failureCount = 0;
    this.updateMetadataOnUserChange();
  }

  // update version and timestamp
  setVersionAndTimestamp(version, lastUpdateTimestamp) {
    this.version = version;
    this.lastUpdateTimestamp = lastUpdateTimestamp;
  }
  getStatus() {
    return this.status;
  }
  visibilityChangeHandler() {
    // cancel existing schedule
    window.clearTimeout(this.timerId);
    this.timerId = undefined;
    // get current tab hidden state (for unsupported browser isTabHidden() will return false), so
    // if isTabHidden true: create a new schedule with a longer interval
    // if isTabHidden false: do a fetchAndReschedule (conditional do a fetch, schedule with normal interval)
    if (Refresh.isTabHidden()) {
      this.schedule();
    } else {
      this.fetchAndReschedule();
    }
  }
  static isTabHidden() {
    return document.visibilityState === 'hidden';
  }

  // bind tab visibility change callback
  bindVisibilityChange() {
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  // unbind tab visibility change callback
  unbindVisibilityChange() {
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  // schedule a fetchAndReschedule
  schedule() {
    this.timerId = window.setTimeout(() => {
      this.fetchAndReschedule();
    }, this.calculateInterval());
  }

  // conditional fetch ffs via fetcher, and schedule another fetchAndReschedule unless we have 400/401 from ffs
  fetchAndReschedule() {
    const fetchedUser = jsonStringfy(this.user);
    if (this.isFetchRequired()) {
      this.fetcher.fetchFeatureFlags(this.user, this.metadataManager.get(), this.version).then(resp => {
        if (fetchedUser === jsonStringfy(this.user)) {
          this.failureCount = 0;
          this.onFeatureFlagUpdate(flagResponseToFlagUpdate(resp));
          this.schedule();
          this.status = RefreshStatus.SUCCESS;
        }
        this.updateMetadataOnRequestComplete();
      }).catch(err => {
        // 401 means client info or apiKey invalid, will have no retry
        // 400 means version data or request body invalid, will have at most `maxInstantRetryTimes` instant retries
        // other 4xx or 5xx will do `maxInstantRetryTimes` instant retries and continue to do backoff retries
        if (err instanceof ResponseError && err.status === 401) {
          // no retry needed
          const message = `Feature flag service returned ${err.status}, "${err.body}". This request will not be retried until the user data has been changed.`;
          this.logger.error(message);
          this.onError(err.status, err.body);
        } else if (err instanceof ResponseError && err.status === 400) {
          if (this.version) {
            // 400 could be caused by corrupted version data, so clear it and schedule a retry
            this.failureCount += 1;
            // a user with no cache will retry instantly for maxInstantRetryTimes
            // onError will be called after all allowed attempts fail
            if (this.failureCount > this.getPollingConfig().maxInstantRetryTimes) {
              this.onError(err.status, err.body);
            }
            this.logger.error(`Feature flag service returned ${err.status}, "${err.body}". Clear version data and retry.`);
            this.setVersionAndTimestamp(undefined, 0);
            this.schedule();
          } else {
            // no retry needed
            const message = `Feature flag service returned ${err.status}, "${err.body}". This request will not be retried until the user data has been changed.`;
            this.logger.error(message);
            this.onError(err.status, err.body);
          }
        } else if (err.name === 'AbortError') {
          this.failureCount += 1;
          // a user with no cache will retry instantly for maxInstantRetryTimes
          // onError will be called after all allowed attempts fail or timeout
          if (this.failureCount > this.getPollingConfig().maxInstantRetryTimes) {
            this.onError(0);
          }
          this.schedule();
        } else {
          this.failureCount += 1;
          // a user with no cache will retry instantly for maxInstantRetryTimes
          // onError will be called after all allowed attempts fail or timeout
          if (this.failureCount > this.getPollingConfig().maxInstantRetryTimes) {
            if (err instanceof ResponseError) {
              this.onError(err.status, err.body);
            } else {
              this.onError();
            }
          }
          this.schedule();
        }
        this.status = RefreshStatus.ERROR;
        this.updateMetadataOnRequestComplete();
      });
    } else {
      this.schedule();
      this.status = RefreshStatus.SUCCESS;
    }
  }
  isFetchRequired() {
    return Date.now() - this.getPollingConfig().interval >= this.lastUpdateTimestamp;
  }
  updateMetadataOnUserChange() {
    // if fetch required, it will be a INITIALIZATION fetch, otherwise it's going to be a delay POLLING fetch
    this.metadataManager.updateClientCauseReason(this.isFetchRequired() ? ClientCauseReason.INITIALIZATION : ClientCauseReason.POLLING);
    // always set user state to SWITCHED user for next fetch
    this.metadataManager.updateClientUserState(ClientUserState.SWITCHED);
  }
  updateMetadataOnInitialisation() {
    // if fetch required, it will be a INITIALIZATION fetch, otherwise it's going to be a delay POLLING fetch
    this.metadataManager.updateClientCauseReason(this.isFetchRequired() ? ClientCauseReason.INITIALIZATION : ClientCauseReason.POLLING);
    // no cache (lastUpdateTimestamp === 0) means we fetch for NEW user, if we have cache we fetch for SAME user
    this.metadataManager.updateClientUserState(this.lastUpdateTimestamp === 0 ? ClientUserState.NEW : ClientUserState.SAME);
  }
  updateMetadataOnRequestComplete() {
    if (this.failureCount) {
      // when current fetch failed, next fetch will be a RETRY
      // the calling user has not changed
      this.metadataManager.updateClientCauseReason(ClientCauseReason.RETRY);
    } else {
      // when current fetch success, next fetch will be a POLLING
      // and the calling user will be SAME user
      this.metadataManager.updateClientCauseReason(ClientCauseReason.POLLING);
      this.metadataManager.updateClientUserState(ClientUserState.SAME);
    }
  }

  // calculate wait interval based on failure count
  calculateInterval() {
    const {
      interval,
      tabHiddenPollingFactor,
      minWaitInterval,
      maxWaitInterval,
      backOffFactor,
      backOffJitter,
      maxInstantRetryTimes
    } = this.getPollingConfig();
    if (maxInstantRetryTimes !== undefined && maxInstantRetryTimes !== 0 && maxInstantRetryTimes >= this.failureCount) {
      return 0;
    }
    if (this.failureCount === 0) {
      return Refresh.isTabHidden() ? interval * tabHiddenPollingFactor : interval;
    }
    let ms = minWaitInterval * backOffFactor ** (this.failureCount - 1);
    if (backOffJitter) {
      const rand = Math.random();
      const deviation = Math.floor(rand * backOffJitter * ms);
      if (Math.floor(rand * 10) < 5) {
        ms -= deviation;
      } else {
        ms += deviation;
      }
    }
    return Number(Math.min(ms, maxWaitInterval));
  }
  getPollingConfig() {
    if (this.version === undefined && this.lastUpdateTimestamp === 0) {
      return this.noCachePollingConfig;
    }
    return this.pollingConfig;
  }
}